import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import '../components/AdminUI.css';

const TYPES_TRANSLATABLE = ['translatable_text', 'translatable_html'];

// Human-readable labels for setting keys. Falls back to a humanized
// version of the key when no entry exists, so new settings still
// render reasonably without code changes.
const SETTING_LABELS = {
  phone_primary: 'Téléphone principal',
  phone_secondary: 'Téléphone secondaire',
  phone_tertiary: 'Téléphone — autre ligne',
  whatsapp: 'WhatsApp',
  email: 'Email de contact',
  address_ma: 'Adresse',
  postal_code_ma: 'Code postal',
  instagram: 'Instagram',
  facebook: 'Facebook',
  google_maps_embed: 'Google Maps — code embed',
  google_maps_link: 'Google Maps — lien partage',
  site_name: 'Nom du site',
  tagline: 'Slogan',
  logo_gold: 'Logo (or)',
  logo_dark: 'Logo (sombre)',
  favicon: 'Favicon',
  urgency_banner: "Bandeau d'urgence",
  cod_enabled: 'Paiement à la livraison activé',
  cmi_enabled: 'CMI activé',
  cmi_clientid: 'CMI — Client ID',
  cmi_storekey: 'CMI — Store Key',
  cmi_endpoint: 'CMI — Endpoint',
  cmi_currency: 'CMI — Code devise',
  free_shipping_enabled: 'Livraison gratuite activée',
  free_shipping_threshold: 'Seuil de livraison gratuite',
  hero_promo_badge: 'Bandeau promo (homepage)',
  currency_label: 'Libellé devise',
  about: 'À propos',
  privacy: 'Politique de confidentialité',
  shipping_policy: 'Politique de livraison',
  refund_policy: 'Politique de retour',
  confirmation_email_subject: 'Sujet — email de confirmation',
};

// Optional hint shown under a setting's input. Use for fields where the
// expected format is non-obvious (embed snippets, monetary thresholds…).
const SETTING_HINTS = {
  google_maps_embed:
    "Collez le snippet HTML complet fourni par Google (« Intégrer une carte → Copier le code HTML ») ou seulement l'URL src=\"…\". Les deux formats sont acceptés.",
  google_maps_link:
    'Lien partagé Google Maps (« Partager → Copier le lien »). Sert pour le bouton « Voir l\'itinéraire ».',
  free_shipping_threshold:
    'Montant minimum (en MAD) à partir duquel la livraison devient gratuite. Mettez 0 pour offrir la livraison sans condition (si activée ci-dessus).',
  cmi_currency: 'Code numérique ISO 4217 — 504 = MAD.',
  urgency_banner: 'Bandeau fin affiché en haut de chaque page. Laissez vide pour le cacher.',
};

const GROUP_LABELS = {
  contact: 'Contact',
  identity: 'Identité',
  payment: 'Paiement',
  shipping: 'Livraison',
  boutique: 'Boutique',
  legal: 'Pages légales',
  rdv: 'Rendez-vous',
};

const humanize = (k) =>
  k.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const labelFor = (s) => s.label || SETTING_LABELS[s.key] || humanize(s.key);

export default function Settings() {
  const [groups, setGroups] = useState({});
  const [tab, setTab] = useState('contact');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    const res = await axios.get('/settings');
    setGroups(res.data || {});
    const first = Object.keys(res.data || {})[0];
    if (first && !res.data[tab]) setTab(first);
  };

  useEffect(() => { load(); }, []);

  const updateField = (key, field, val) => {
    setGroups((g) => {
      const clone = JSON.parse(JSON.stringify(g));
      Object.keys(clone).forEach((grp) => {
        clone[grp] = clone[grp].map((s) => s.key === key ? { ...s, [field]: val } : s);
      });
      return clone;
    });
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const all = Object.values(groups).flat();
      await axios.put('/settings', {
        settings: all.map(({ key, value, value_fr, value_ar, value_en }) => ({ key, value, value_fr, value_ar, value_en })),
      });
      setMsg('Enregistré');
      setTimeout(() => setMsg(null), 2000);
    } catch (e) {
      setMsg('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const tabs = Object.keys(groups);

  const renderField = (s) => {
    if (s.type === 'bool') {
      return (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
          <input type="checkbox" checked={!!parseInt(s.value)} onChange={(e) => updateField(s.key, 'value', e.target.checked ? '1' : '0')} />
          {parseInt(s.value) ? 'Activé' : 'Désactivé'}
        </label>
      );
    }
    if (s.type === 'image') {
      return (
        <div>
          <input className="admin-input" placeholder="URL de l'image" value={s.value || ''} onChange={(e) => updateField(s.key, 'value', e.target.value)} />
          {s.value && <img src={assetUrl(s.value)} alt="" style={{ maxWidth: 120, marginTop: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />}
        </div>
      );
    }
    if (TYPES_TRANSLATABLE.includes(s.type)) {
      return (
        <div className="admin-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {['fr', 'ar', 'en'].map((l) => (
            <div key={l}>
              <label className="admin-label">{l.toUpperCase()}</label>
              {s.type === 'translatable_html' ? (
                <textarea
                  className="admin-textarea" rows={4} dir={l === 'ar' ? 'rtl' : 'ltr'}
                  value={s[`value_${l}`] || ''}
                  onChange={(e) => updateField(s.key, `value_${l}`, e.target.value)}
                />
              ) : (
                <input
                  className="admin-input" dir={l === 'ar' ? 'rtl' : 'ltr'}
                  value={s[`value_${l}`] || ''}
                  onChange={(e) => updateField(s.key, `value_${l}`, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      );
    }
    if (s.type === 'text' || s.type === 'html') {
      return <textarea className="admin-textarea" rows={3} value={s.value || ''} onChange={(e) => updateField(s.key, 'value', e.target.value)} />;
    }
    return <input className="admin-input" value={s.value || ''} onChange={(e) => updateField(s.key, 'value', e.target.value)} />;
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h2>Paramètres</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {msg && <span style={{ fontSize: 13, color: msg.includes('Erreur') ? '#dc2626' : '#16a34a' }}>{msg}</span>}
          <button className="admin-btn admin-btn-gold" disabled={saving} onClick={save}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        {tabs.map((g) => (
          <button key={g} className={`admin-tab ${tab === g ? 'active' : ''}`} onClick={() => setTab(g)}>
            {GROUP_LABELS[g] || humanize(g)}
          </button>
        ))}
      </div>

      <div className="admin-card">
        {(groups[tab] || []).map((s) => (
          <div key={s.key} className="settings-field">
            <label className="settings-field-label">{labelFor(s)}</label>
            {renderField(s)}
            {SETTING_HINTS[s.key] && (
              <p className="settings-field-hint">{SETTING_HINTS[s.key]}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
